import React, { useState, useEffect } from 'react';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { User, Mail, Lock, AlertTriangle, Eye, EyeOff, Trash2 } from 'lucide-react';

const Profil = ({ user, setUser }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    prenom: user.prenom,
    nom: user.nom,
    telephone: user.telephone || '',
    email: user.email,
  });

  // Modal changement de mot de passe
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Modal suppression de compte
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Vérifier si l'utilisateur doit changer son mot de passe
  const mustChangePassword = user.must_change_password === true;

  useEffect(() => {
    if (mustChangePassword) {
      setPasswordModal(true);
      toast.warning('Vous devez changer votre mot de passe provisoire');
    }
  }, [mustChangePassword]);

  const handleSave = async () => {
    try {
      const response = await apiClient.put('/auth/update-profile', {
        prenom: formData.prenom,
        nom: formData.nom,
        telephone: formData.telephone,
        email: formData.email
      });
      
      const updatedUser = { ...user, ...response.data };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profil mis à jour');
      setEditMode(false);
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la mise à jour');
      if (message) toast.error(message);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }

    try {
      await apiClient.put('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast.success('Mot de passe modifié avec succès');
      setPasswordModal(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      
      // Mettre à jour le flag must_change_password
      const updatedUser = { ...user, must_change_password: false };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors du changement de mot de passe');
      if (message) toast.error(message);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') {
      toast.error('Veuillez taper SUPPRIMER pour confirmer');
      return;
    }

    if (!deletePassword) {
      toast.error('Veuillez entrer votre mot de passe');
      return;
    }

    setIsDeleting(true);
    try {
      await apiClient.delete('/auth/delete-account', {
        data: { password: deletePassword }
      });
      
      toast.success('Compte supprimé avec succès');
      
      // Déconnexion
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la suppression du compte');
      if (message) toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fade-in max-w-2xl" data-testid="profil-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Mon profil</h1>
        <p className="text-slate-500 text-lg">Informations personnelles</p>
      </div>

      {/* Alerte si mot de passe à changer */}
      {mustChangePassword && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            Vous utilisez un mot de passe provisoire. Veuillez le changer pour sécuriser votre compte.
          </p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8">
        <div className="flex items-center gap-4 mb-8 pb-8 border-b border-slate-200">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-indigo-600" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              {user.prenom} {user.nom}
            </h2>
            <p className="text-slate-500 capitalize">{user.role}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label>Prénom</Label>
              {editMode ? (
                <Input
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  data-testid="profil-prenom-input"
                />
              ) : (
                <p className="text-slate-900 font-medium mt-2">{user.prenom}</p>
              )}
            </div>
            <div>
              <Label>Nom</Label>
              {editMode ? (
                <Input
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  data-testid="profil-nom-input"
                />
              ) : (
                <p className="text-slate-900 font-medium mt-2">{user.nom}</p>
              )}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </Label>
            {editMode ? (
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="profil-email-input"
              />
            ) : (
              <p className="text-slate-900 font-medium mt-2">{user.email}</p>
            )}
          </div>

          <div>
            <Label>Téléphone</Label>
            {editMode ? (
              <Input
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                data-testid="profil-telephone-input"
              />
            ) : (
              <p className="text-slate-900 font-medium mt-2">{user.telephone || 'Non renseigné'}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label>Lot</Label>
              <p className="text-slate-900 font-medium mt-2">{user.lot || 'Non renseigné'}</p>
            </div>
            <div>
              <Label>Tantièmes</Label>
              <p className="text-slate-900 font-medium mt-2">{user.tantiemes}</p>
            </div>
          </div>

          {/* Section Sécurité */}
          <div className="pt-6 border-t border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Sécurité
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setPasswordModal(true)}
                variant="outline"
                className={mustChangePassword ? 'border-amber-500 text-amber-600 hover:bg-amber-50' : ''}
                data-testid="change-password-button"
              >
                <Lock className="w-4 h-4 mr-2" />
                {mustChangePassword ? 'Changer le mot de passe provisoire' : 'Modifier le mot de passe'}
              </Button>
              <Button
                onClick={() => setDeleteModal(true)}
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50"
                data-testid="delete-account-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer le compte
              </Button>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-200">
            {editMode ? (
              <div className="flex gap-3">
                <Button
                  onClick={handleSave}
                  data-testid="save-profil-button"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  Enregistrer
                </Button>
                <Button
                  onClick={() => {
                    setEditMode(false);
                    setFormData({
                      prenom: user.prenom,
                      nom: user.nom,
                      telephone: user.telephone || '',
                      email: user.email,
                    });
                  }}
                  variant="outline"
                  data-testid="cancel-profil-button"
                >
                  Annuler
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setEditMode(true)}
                data-testid="edit-profil-button"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                Modifier le profil
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Modal changement de mot de passe */}
      <Dialog open={passwordModal} onOpenChange={(open) => {
        if (!mustChangePassword || open) setPasswordModal(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-indigo-600" />
              {mustChangePassword ? 'Créez votre mot de passe' : 'Modifier le mot de passe'}
            </DialogTitle>
            <DialogDescription>
              {mustChangePassword 
                ? 'Vous utilisez un mot de passe provisoire. Créez votre propre mot de passe sécurisé.'
                : 'Entrez votre mot de passe actuel puis le nouveau mot de passe souhaité.'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {!mustChangePassword && (
              <div>
                <Label>Mot de passe actuel</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            <div>
              <Label>Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Minimum 6 caractères"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label>Confirmer le nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Retapez le mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                <p className="text-red-500 text-xs mt-1">Les mots de passe ne correspondent pas</p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              {!mustChangePassword && (
                <Button onClick={() => setPasswordModal(false)} variant="outline" className="flex-1">
                  Annuler
                </Button>
              )}
              <Button
                onClick={handlePasswordChange}
                disabled={!passwordData.new_password || passwordData.new_password !== passwordData.confirm_password}
                className={`flex-1 ${mustChangePassword ? 'w-full' : ''} bg-indigo-600 hover:bg-indigo-700`}
              >
                {mustChangePassword ? 'Créer mon mot de passe' : 'Modifier'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal suppression de compte */}
      <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Supprimer mon compte
            </DialogTitle>
            <DialogDescription>
              Cette action est <strong>définitive et irréversible</strong>. Toutes vos données seront supprimées.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">
                En supprimant votre compte :
              </p>
              <ul className="text-sm text-red-600 mt-2 list-disc list-inside">
                <li>Votre profil sera définitivement effacé</li>
                <li>Vous serez retiré de toutes les copropriétés</li>
                <li>Vos données ne pourront pas être récupérées</li>
              </ul>
            </div>

            <div>
              <Label>Tapez <strong>SUPPRIMER</strong> pour confirmer</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="SUPPRIMER"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Votre mot de passe</Label>
              <Input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Entrez votre mot de passe"
                className="mt-1"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                onClick={() => {
                  setDeleteModal(false);
                  setDeletePassword('');
                  setDeleteConfirmText('');
                }} 
                variant="outline" 
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'SUPPRIMER' || !deletePassword || isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeleting ? 'Suppression...' : 'Supprimer définitivement'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profil;
